import {ChangeEvent, HTMLAttributes, MouseEvent, useRef, useState} from 'react';
import styled from 'styled-components';
import {useBackgroundClick} from '@/hooks';
import {FlexFullGrow} from '@/styled-components';
import {interceptProps} from '@/styled-components/utils';
import {palette} from '@/styles';
import {Checkbox, CheckboxItem, CheckboxLabel} from '../Checkbox';
import {CheckIcon, ChevronDownIcon} from '../Icons';
import {Typography, typographyStyle} from '../Typography';

interface IOption {
  label: string;
  value: any;
}

interface IProps extends HTMLAttributes<HTMLDivElement> {
  size?: 'small' | 'medium' | 'large';
  label?: string;
  required?: boolean;
  placeholder?: string;
  activePlaceholder?: boolean; // placeholder값을 활성화 값처럼 인식함 (텍스트색상 진하게)
  list?: IOption[];
  selected?: IOption[];
  disabled?: boolean;
  readOnly?: boolean;
  fullWidth?: boolean;
  multi?: boolean;
  dropdownListStyle?: React.CSSProperties;
  onSelected?(selected: IOption[]): void;
}

export const SelectList = ({
  size,
  label,
  required,
  placeholder,
  activePlaceholder,
  selected,
  list,
  disabled,
  readOnly,
  fullWidth,
  multi,
  dropdownListStyle,
  onSelected,
  ...props
}: IProps) => {
  const listRef = useRef<HTMLDivElement>(null);
  const selectorRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState<boolean>(false);

  const hasSelected = !!selected?.length;
  const hasList = !!list?.length;

  const handleClickText = (e: MouseEvent<HTMLDivElement>) => {
    if (readOnly) {
      return;
    }

    e.stopPropagation();

    if (!disabled) {
      setOpen((prev) => !prev);
    }
  };

  const handleSelected = (item: IOption) => {
    if (!disabled) {
      setOpen(false);
      onSelected?.([item]);
    }
  };

  const handleChecked = (e: ChangeEvent<HTMLInputElement>) => {
    if (!onSelected || !list) {
      return;
    }

    const {name, checked} = e.target;
    const prevSelected = selected || [];

    if (checked) {
      const item = list.find((i) => i.value === name);

      if (item) {
        onSelected([...prevSelected, item]);
      }
    } else {
      onSelected(prevSelected.filter((i) => i.value !== name));
    }
  };

  useBackgroundClick(
    listRef,
    () => {
      setOpen(false);
    },
    [selectorRef]
  );

  const hasLabel = !!label?.length;

  return (
    <Root fullWidth={fullWidth} hasLabel={hasLabel} {...props}>
      {hasLabel && (
        <Label variant="medium12" color={palette.gray600}>
          {label}
          {required && <Typography color={palette.red100}>*</Typography>}
        </Label>
      )}
      <Selector
        ref={selectorRef}
        active={open}
        size={size}
        disabled={disabled}
        onClick={handleClickText}
      >
        <Typography
          variant={size === 'small' ? 'medium12' : 'medium14'}
          color={
            disabled
              ? palette.disabled
              : hasSelected || activePlaceholder
              ? palette.textPrimary
              : palette.placeholder
          }
          ellipsis
        >
          {hasSelected ? selected.map((i) => i.label).join(', ') : placeholder}
        </Typography>
        <FlexFullGrow />
        <Arrow src={ChevronDownIcon} style={{opacity: disabled ? 0.2 : 1}} />
      </Selector>
      {open && hasList && (
        <List ref={listRef} style={dropdownListStyle}>
          {list.map((item) => {
            const active =
              hasSelected &&
              selected.findIndex((i) => i.value === item.value) >= 0;
            return (
              <SelectListItem
                key={item.value}
                active={!multi && active}
                size={size}
                onClick={!multi ? () => handleSelected(item) : undefined}
              >
                {multi ? (
                  <CheckboxItem>
                    <Checkbox
                      id={`checkbox-selected-list-${item.value}`}
                      name={item.value}
                      checked={active}
                      onChange={handleChecked}
                    />
                    <CheckboxLabel>{item.label}</CheckboxLabel>
                  </CheckboxItem>
                ) : (
                  item.label
                )}
              </SelectListItem>
            );
          })}
        </List>
      )}
    </Root>
  );
};

const Root = styled('div').withConfig({
  shouldForwardProp: interceptProps(['fullWidth', 'hasLabel']),
})<{
  fullWidth?: boolean;
  hasLabel?: boolean;
}>`
  position: relative;
  ${({fullWidth}) => fullWidth && `width: 100%;`}
  ${({hasLabel}) => hasLabel && `padding-top: 8px;`}
`;

const Arrow = styled.img`
  transition: all 0.3s;
  flex-shrink: 0;
  margin-left: 4px;
`;

const Selector = styled('div').withConfig({
  shouldForwardProp: interceptProps(['active', 'size', 'disabled']),
})<{
  active?: boolean;
  size?: IProps['size'];
  disabled?: boolean;
}>`
  ${({size}) =>
    size === 'small'
      ? `padding: 4px 8px`
      : size === 'large'
      ? `padding: 11px 15px`
      : `padding: 8px`};

  display: flex;
  align-items: center;
  width: 100%;
  border-radius: 3px;
  border: 1px solid ${palette.gray300};
  background-color: ${palette.white};
  cursor: pointer;
  ${Typography} {
    flex-grow: 1;
  }
  ${({active}) =>
    active &&
    `
    ${Arrow} {
      transform: rotate(180deg);
    }
    box-shadow: 0px 0px 4px 0px #4a81d5;
    border-color: #4a81d5;
  `}
  ${({disabled}) =>
    disabled
      ? `
    background-color: ${palette.gray100};
    border-color: ${palette.gray100};
    cursor: default;
    `
      : `
      &:not(.active):hover {
      border-color: ${palette.gray400};
    }
  `}
`;

const List = styled.div`
  position: absolute;
  left: 0;
  top: 100%;
  width: 100%;
  max-height: 360px;
  overflow: auto;
  background-color: ${palette.white};
  border: solid 1px ${palette.gray300};
  border-radius: 5px;
  z-index: 1;
`;

export const SelectListItem = styled('div').withConfig({
  shouldForwardProp: interceptProps(['active', 'size']),
})<{
  active?: boolean;
  size?: IProps['size'];
}>`
  position: relative;
  ${({size}) => (size === 'small' ? `padding: 4px 8px;` : `padding: 8px;`)}
  color: ${palette.textPrimary};
  white-space: nowrap;
  cursor: pointer;
  font-weight: 500;
  color: ${palette.textPrimary};
  ${({size}) =>
    size === 'small'
      ? {...typographyStyle.medium12}
      : {...typographyStyle.medium14}}
  &:hover {
    background-color: ${palette.gray100};
  }
  ${({active}) =>
    active &&
    `
    color: ${palette.teal100};
    display: flex;
    align-items: center;
    justify-content: space-between;
    ::after {
      content: '';
      width: 16px;
      height: 16px;
      margin-left: 4px;
      flex-shrink: 0;
      mask-repeat: no-repeat;
      mask-position: center;
      mask-size: contain;
      background-color: ${palette.teal100};
      mask-image: url(${CheckIcon});
    }
  `}
  & + & {
    ::before {
      content: '';
      position: absolute;
      top: 0;
      height: 1px;
      left: 8px;
      right: 8px;
      background-color: ${palette.gray200};
    }
  }
  ${CheckboxLabel} {
    padding-left: 4px;
    cursor: pointer;
  }
`;

const Label = styled(Typography)`
  display: inline-block;
  position: absolute;
  pointer-events: none;
  top: 0;
  left: 7px;
  padding: 0 1px;
  background-color: ${palette.white};
`;
